import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', backgroundColor: '#ffffff' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  logo: { width: 80, height: 80, objectFit: 'contain' },
  companyInfo: { textAlign: 'right', maxWidth: '45%' },
  companyName: { fontSize: 14, fontWeight: 'bold', marginBottom: 3 },
  companyDetails: { fontSize: 9, color: '#666', marginBottom: 2 },
  title: { fontSize: 26, fontWeight: 'bold', color: '#10b981', marginBottom: 4 },
  subtitle: { fontSize: 12, color: '#666', marginBottom: 18 },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16, padding: 12, backgroundColor: '#f0fdf4', borderRadius: 6 },
  infoBlock: { width: '48%' },
  label: { fontSize: 9, color: '#888', marginBottom: 3, textTransform: 'uppercase' },
  value: { fontSize: 11, marginBottom: 6, fontWeight: 'bold' },
  tableHeader: { flexDirection: 'row', backgroundColor: '#10b981', color: '#ffffff', padding: 8, fontWeight: 'bold', fontSize: 9 },
  tableRow: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 8, borderBottomWidth: 1, borderColor: '#f0f0f0', fontSize: 9 },
  colProduct: { width: '40%' },
  colQty: { width: '15%', textAlign: 'center' },
  colCost: { width: '20%', textAlign: 'right' },
  colTotal: { width: '25%', textAlign: 'right', fontWeight: 'bold' },
  totalsSection: { marginTop: 16, alignItems: 'flex-end' },
  grandTotal: { backgroundColor: '#10b981', color: '#ffffff', padding: 10, fontSize: 13, fontWeight: 'bold', flexDirection: 'row', justifyContent: 'space-between', width: '45%' },
  footer: { position: 'absolute' as const, bottom: 30, left: 40, right: 40, textAlign: 'center' as const, fontSize: 8, color: '#999', borderTopWidth: 1, borderColor: '#e0e0e0', paddingTop: 10 },
});

export interface GrnPdfData {
  grn_number: string;
  po_number: string;
  supplier_name?: string;
  received_at?: string;
  received_by_name?: string;
  lines: Array<{
    product_name: string;
    sku?: string;
    quantity_received: number;
    unit_cost: number;
    line_total?: number;
  }>;
  total: number;
}

const GRNPdf: React.FC<{ grn: GrnPdfData; businessSettings?: any }> = ({ grn, businessSettings }) => {
  const { API_URL } = require('../config/api');
  const logoUrl = businessSettings?.logo?.startsWith('http')
    ? businessSettings.logo
    : businessSettings?.logo
      ? `${API_URL.replace('/api', '')}${businessSettings.logo}`
      : null;

  const receivedDate = grn.received_at
    ? new Date(grn.received_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View>
            {logoUrl && <Image src={logoUrl} style={styles.logo} />}
          </View>
          <View style={styles.companyInfo}>
            {businessSettings?.name && <Text style={styles.companyName}>{businessSettings.name}</Text>}
            {businessSettings?.email && <Text style={styles.companyDetails}>{businessSettings.email}</Text>}
            {businessSettings?.phone && <Text style={styles.companyDetails}>Tel: {businessSettings.phone}</Text>}
          </View>
        </View>

        <Text style={styles.title}>GOODS RECEIVED NOTE</Text>
        <Text style={styles.subtitle}>{grn.grn_number}</Text>

        <View style={styles.infoRow}>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>Purchase Order</Text>
            <Text style={styles.value}>{grn.po_number}</Text>
            <Text style={styles.label}>Supplier</Text>
            <Text style={styles.value}>{grn.supplier_name || 'N/A'}</Text>
          </View>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>Received Date</Text>
            <Text style={styles.value}>{receivedDate}</Text>
            <Text style={styles.label}>Received By</Text>
            <Text style={styles.value}>{grn.received_by_name || 'N/A'}</Text>
          </View>
        </View>

        <View style={styles.tableHeader}>
          <Text style={styles.colProduct}>Product</Text>
          <Text style={styles.colQty}>Qty Received</Text>
          <Text style={styles.colCost}>Unit Cost</Text>
          <Text style={styles.colTotal}>Line Total</Text>
        </View>
        {grn.lines.map((line, idx) => {
          const lineTotal = line.line_total ?? line.quantity_received * line.unit_cost;
          return (
            <View key={idx} style={styles.tableRow}>
              <Text style={styles.colProduct}>
                {line.product_name}{line.sku ? ` (${line.sku})` : ''}
              </Text>
              <Text style={styles.colQty}>{line.quantity_received}</Text>
              <Text style={styles.colCost}>£{line.unit_cost.toFixed(2)}</Text>
              <Text style={styles.colTotal}>£{lineTotal.toFixed(2)}</Text>
            </View>
          );
        })}

        <View style={styles.totalsSection}>
          <View style={styles.grandTotal}>
            <Text>TOTAL RECEIVED VALUE:</Text>
            <Text>£{grn.total.toFixed(2)}</Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>Goods Received Note — retain for your records and supplier reconciliation</Text>
        </View>
      </Page>
    </Document>
  );
};

export default GRNPdf;
